import React from 'react';
import { useTranslation } from 'next-i18next';
import classnames from 'classnames';

import { ListingBanner } from 'components/layout';
import { ProductsCarousel } from 'components/product';
import { ImageMessage } from 'components/ui';
import { IMAGES } from 'constants/images';
import { createObjectMap } from 'helpers/ObjectHelpers';
import { parseBanner } from 'helpers/ProductsListingHelpers';
import { useRouteQueryAnalyticsListProps } from 'hooks/useRouteQueryAnalyticsListProps';

import type { ICategoryPageUrlParams, ICategoryProductsListing } from 'types';

import styles from './MainCategoryTemplate.module.scss';

const SLOT_TYPES = {
  PRODUCTS_CAROUSEL: 'products_carousel',
};

const SLOTS_MAP = {
  [SLOT_TYPES.PRODUCTS_CAROUSEL]: ProductsCarousel
};

interface Props {
  category: ICategoryProductsListing['payload'],
  urlParams: ICategoryPageUrlParams
}

// TODO: add types

const MainCategoryTemplate = React.memo((props: Props) => {

  const {
    category
  } = props;

  const {
    slots: categorySlots = [],
    banners = []
  } = category;

  // Hooks

  const { t } = useTranslation('common');

  const {
    analyticsListProps: listProps,
    persist: persistAnalyticsListProps
  } = useRouteQueryAnalyticsListProps();

  // Map slots by type

  const slots = React.useMemo(() => {
    return categorySlots?.reduce((acc, slot) => {
      const { type } = slot;

      if (type === SLOT_TYPES.PRODUCTS_CAROUSEL) {
        const {
          analyticsListId,
          analyticsListName,
          title,
          products,
          link
        } = slot;

        if (!products?.length) return acc;

        const productListProps = listProps || {
          analyticsListId,
          analyticsListName
        };

        const productCardProps = {
          bypassQuantityVariants: true
        };

        acc.push({
          Component: SLOTS_MAP[type],
          componentProps: {
            title,
            link,
            products,
            productCardProps,
            productListProps,
            onLinkClick: () => {
              persistAnalyticsListProps();
            }
          }
        });
      }

      return acc;
    }, []);
  }, [categorySlots, listProps, persistAnalyticsListProps]);

  // Map banners by position

  const bannersByPosition = React.useMemo(() => {
    return createObjectMap(
      banners?.map(parseBanner),
      'position',
      false
    );
  }, [banners]);

  // Empty State

  if (slots?.length === 0) {
    return (
      <ImageMessage
        className={styles.emptyContainer}
        image={IMAGES.PLACEHOLDERS.NO_PRODUCTS}
        imageProps={{
          width: 414,
          height: 246,
          alt: 'No products'
        }}
        title={t('CATEGORIES.EMPTY_TITLE')}
      />
    );
  }

  // Render

  return slots?.map((slot, index) => {
    const { Component, componentProps } = slot;

    if (!Component) return null;

    const banner = bannersByPosition[index];
    const nextBanner = bannersByPosition[index + 1];
    const isLastSlot = (index === slots.length - 1);

    return (
      <React.Fragment key={`${index}`}>

        {/* Current Banner */}
        {
          banner && (
            <ListingBanner
              className={classnames(styles.banner, { [styles.first]: index === 0 })}
              position={index}
              banner={banner}
            />
          )
        }

        {/* Slot */}
        <Component
          {...componentProps}
        />

        {/* Next Banner */}
        {
          (isLastSlot && nextBanner) && (
            <ListingBanner
              className={classnames(styles.banner, { [styles.last]: isLastSlot })}
              position={index + 1}
              banner={nextBanner}
            />
          )
        }

      </React.Fragment>
    );
  });
});

export default MainCategoryTemplate;
